import type { TranslationCatalog } from "../types.js";
import { pluralBcs } from "../plural.js";

function coinWord(n: number): string {
  return pluralBcs(n, ["новчић", "новчића", "новчића"]);
}

export default {
  "common.loading": "Повезивање...",
  "common.loadingEllipsis": "Учитавање...",
  "common.unknownError": "Непозната грешка",
  "common.errorPrefix": (p: { message: string }) => `Грешка: ${p.message}`,
  "common.retry": "Покушај поново",
  "common.close": "Затвори",
  "common.back": "← Назад",
  "common.cancel": "Откажи",
  "common.details": "Детаљи",
  "common.settingsTitle": "⚙️ Подешавања",
  "common.profile": "👤 Профил",
  "common.rules": "📖 Правила",

  "login.subtitle": "Пријави се да играш.",
  "login.continueFacebook": "Настави са Facebook налогом",
  "login.continueGoogle": "Настави са Google налогом",
  "login.continueEmail": "Настави са имејл адресом",
  "login.tagline": "Адутска карташка игра са лицитацијом за три играча. Лицитирај, изабери адута и однеси штихове — први до 101 поена побеђује.",
  "login.linkRules": "Правила",
  "login.linkStrategy": "Стратегија",
  "login.linkAbout": "О игри",
  "login.linkPrivacy": "Приватност",

  "lobby.entryFee": (p: { fee: number }) => `Улазница по партији: ${p.fee} ${coinWord(p.fee)}`,
  "lobby.resumeMatch": "Настави партију",
  "lobby.playButton": "Играј (3 играча)",
  "lobby.inviteFriend": "📣 Позови пријатеља",
  "lobby.insufficientFunds": (p: { missing: number }) => `Немаш довољно новчића за улазницу (недостаје ${p.missing}).`,
  "lobby.watchAd": "Гледај рекламу за додатне новчиће",

  "leaderboard.title": "🏆 Ранг листа",
  "leaderboard.emptySeason": "Још нема рангираних играча ове сезоне (потребно је одиграти бар 1 партију).",
  "leaderboard.emptyGlobal": "Још нема рангираних играча (потребно је одиграти бар 1 партију укупно).",
  "leaderboard.seasonTabDefault": "Сезона",
  "leaderboard.seasonTabWithDate": (p: { month: string; year: string }) => `Сезона ${p.month}/${p.year}`,
  "leaderboard.globalTab": "🌍 Укупно (све сезоне)",
  "leaderboard.colPlayer": "Играч",
  "leaderboard.colPoints": "Бодови",
  "leaderboard.colMatches": "Партије",
  "leaderboard.you": "Ти",
  "leaderboard.yourScoreUnranked": (p: { points: number; matchesPlayed: number }) =>
    `Твој скор: ${p.points} бодова из ${p.matchesPlayed} партија (треба бар 1 партију за ранг)`,

  "rules.title": "📖 Како се игра Мексико",
  "rules.cardsHeading": "Карте",
  "rules.cardsBody":
    "Игра се са 32 карте (7, 8, 9, 10, J, Q, K, A у све 4 боје). Јачина карата унутар боје, од најслабије: 7 &lt; 8 &lt; 9 &lt; 10 &lt; J &lt; Q &lt; K &lt; A. Адутска боја носи све неадутске карте, без обзира на ранг.",
  "rules.dealingHeading": "Дељење",
  "rules.dealingBody": 'Сваки од 3 играча добија 10 карата (у два круга по 5), а 2 карте остају скривене у "купу".',
  "rules.biddingHeading": "Лицитација",
  "rules.biddingBody":
    'Први играч мора лицитирати бар 5. Сваки следећи мора лицитирати строго више (5, 6, 7, 8, 9, 10, или "МЕКСИКО") или рећи "даље" — ко једном каже "даље", трајно испада из лицитације за то дељење. Лицитација се завршава кад остане само један играч.',
  "rules.talonHeading": "Куп и шкарт",
  "rules.talonBody":
    "Победник лицитације отвара куп (2 карте, видљиве свима), додаје их својој руци (сад има 12 карата), па шкартира 2 карте назад лицем надоле. Затим бира адутску боју.",
  "rules.mexicoHeading": "МЕКСИКО (20)",
  "rules.mexicoBody":
    'Ко лицитира "МЕКСИКО", обавезује се да однесе свих 10 штихова. Игра се без адута и без купа — само са оригиналних 10 карата.',
  "rules.tricksHeading": "Ток игре (штихови)",
  "rules.tricksBody":
    "Мора се пратити изнета боја ако је имаш. Ако је немаш, мораш бацити адута ако га имаш (сечење) — тек ако немаш ни једно ни друго, можеш бацити било шта. Штих носи најјача карта изнесене боје, осим ако је неко бацио адута — тада најјачи адут носи, без обзира на боју. Ко однесе штих, води у следећем.",
  "rules.scoringHeading": "Бодовање",
  "rules.scoringBody":
    "Победник лицитације: ако однесе бар онолико штихова колико је лицитирао, добија онолико поена колико је стварно однео. Ако не успе, добија <strong>негативне</strong> поене у висини лицитације. Остала два играча увек добијају поене једнаке броју штихова које су однели.",
  "rules.matchEndHeading": "Крај партије",
  "rules.matchEndBody":
    "Партија се завршава кад неко достигне циљни скор. Ако више играча пређе циљ у истом дељењу са истим највишим скором, игра се додатно дељење док се не разреши.",

  "settings.soundOff": "🔇 Звук искључен",
  "settings.soundOn": "🔊 Звук укључен",
  "settings.displayNameLabel": "Име у игри",
  "settings.saveDisplayName": "Сачувај име",
  "settings.displayNameLengthError": "Име мора имати 1-20 карактера.",
  "settings.saveFailed": "Чување није успело, покушај поново.",
  "settings.logout": "🚪 Одјави се",

  "waitingRoom.waitingForPlayers": (p: { filled: number; total: number }) => `Чекање играча... ${p.filled}/${p.total}`,
  "waitingRoom.countdown": (p: { remainingSec: number }) => `Остало ${p.remainingSec}с пре него што АИ попуни празна места`,

  "game.passLabel": "даље",
  "game.passButton": "Даље",
  "game.mexicoLabel": "МЕКСИКО",
  "game.bidLabel": (p: { bid: string }) => `Лицитација: ${p.bid}`,
  "game.trumpLabel": (p: { suit: string }) => `Адут: ${p.suit}`,
  "game.tricksWonLabel": (p: { count: number }) => `Штихова: ${p.count}`,
  "game.biddingInProgress": "Лицитација у току...",
  "game.yourBid": "Твоја лицитација",
  "game.someoneDiscarding": (p: { name: string }) => `${p.name} шкартира...`,
  "game.someoneChoosingTrump": (p: { name: string }) => `${p.name} бира адут...`,
  "game.chooseTrumpPrompt": "Изабери адут",
  "game.nextDealPause": "Следеће дељење...",
  "game.fromTalon": "из купа",
  "game.talonRevealLabel": (p: { bidder: string }) => `Карте из купа (${p.bidder})`,
  "game.yourTurnLabel": "Твој потез",
  "game.turnTimerLabel": (p: { name: string; remainingSec: number }) => `${p.name} — ${p.remainingSec}с`,
  "game.idleBotTakeover": "🤖 Ниси на време одиграо/ла потез, па АИ тренутно игра уместо тебе. Кликни било где да преузмеш контролу.",
  "game.leaveMatch": "Напусти партију",
  "game.cardAlt": "карта",
  "game.botDisplayName": "АИ играч",
  "game.botBadgeIdle": "🤖 АИ (привремено)",
  "game.botBadgeFull": "🤖 БОТ",
  "game.declarerTooltip": "Води лицитацију",

  "matchEnd.title": "Партија завршена",
  "matchEnd.winner": (p: { name: string }) => `Победник: ${p.name}`,
  "matchEnd.backToLobby": "Назад у лоби",

  "reactions.sendButton": "Пошаљи реакцију",
  "reaction.angry_small_card": "Зашто малом картом?",
  "reaction.angry_big_card": "Зашто великом картом?",
  "reaction.bravo": "Браво",
  "reaction.svaka_cast": "Свака част",
  "reaction.cestitam_pobeda": "Честитам на победи",
  "reaction.izvini": "Извини",
  "reaction.pa_zasto": "Па зашто!",
  "reaction.srecan": "",
  "reaction.srce": "",
  "reaction.pozdrav": "",

  "medal.season_gold": "🥇 Сезона",
  "medal.season_silver": "🥈 Сезона",
  "medal.season_bronze": "🥉 Сезона",
  "medal.first_match": "🎉 Прва партија",
  "medal.matches_100": "💯 100 партија",
  "medal.mexico_master": "🌵 Мексико мајстор",
  "medal.hat_trick_3_wins": "🔥 Хетрик (3 победе)",
  "medal.tricks_1000": "🃏 1000 штихова",

  "suit.diamonds": "Каро",
  "suit.hearts": "Срце",
  "suit.clubs": "Треф",
  "suit.leaves": "Лист",

  "profile.matchesPlayed": "Одиграних партија",
  "profile.firstPlace": (p: { wins: number }) => `🥇 1. место (${p.wins})`,
  "profile.secondPlace": (p: { count: number }) => `🥈 2. место (${p.count})`,
  "profile.thirdPlace": (p: { count: number }) => `🥉 3. место (${p.count})`,
  "profile.pointsConfigNote": (p: { win: number; second: number; third: number }) =>
    `Бодовање по партији: 1. место ${p.win}, 2. место ${p.second}, 3. место ${p.third} поена`,
  "profile.noMedals": "Још нема освојених медаља.",
  "profile.medalsHeading": "Медаље",
  "profile.seasonHistoryHeading": "Историја сезона",
  "profile.colSeason": "Сезона",
  "profile.colRank": "Ранг",
  "profile.deleteAccountButton": "🗑️ Обриши налог",
  "profile.deleteAccountTitle": "Обриши налог",
  "profile.deleteAccountWarning":
    "Ово трајно брише твој налог и све податке (историју партија, ранг листу, медаље, стање новчића). Ова акција се не може поништити.",
  "profile.deleteAccountConfirm": "Да, обриши налог",
  "profile.deleteAccountFailed": "Брисање није успело, покушај поново.",
  "profile.deleteAccountActiveMatch": "Не можеш обрисати налог док си у активној партији. Заврши или напусти партију па покушај поново.",

  "errors.NOT_YOUR_TURN": "Није твој ред.",
  "errors.INVALID_BID": "Неважећа лицитација.",
  "errors.BID_TOO_LOW": "Лицитација мора бити већа од тренутно највише.",
  "errors.MUST_BID_FIRST": "Први играч мора лицитирати бар 5, не може рећи даље.",
  "errors.DUPLICATE_DISCARD": "Не можеш одбацити исту карту два пута.",
  "errors.CARD_NOT_IN_HAND": "Та карта није у твојој руци.",
  "errors.MUST_FOLLOW_SUIT": "Мораш пратити боју, или сећи адутом ако не можеш да пратиш.",
  "errors.TRICK_INCOMPLETE": "Штих још није завршен.",
  "errors.MATCH_ENDED": "Партија је већ завршена.",
  "errors.UNKNOWN_ACTION": "Непозната акција.",
  "errors.WRONG_PHASE": "Та акција није дозвољена у овој фази игре.",
  "errors.NOT_BID_WINNER": "Само победник лицитације може то да уради.",
  "errors.MATCH_NOT_ENDED": "Партија још није завршена.",
  "errors.invalid_session_token": "Сесија је истекла, пријави се поново.",
  "errors.room_full": "Сто је попуњен, покушај поново за тренутак.",
  "errors.insufficient_balance": "Немаш довољно новчића за улазницу.",
  "errors.ad_unavailable": "Реклама тренутно није доступна. Провери интернет конекцију (или искључи AdBlock) и покушај поново.",
  "errors.invalid_or_early_ticket": "Реклама није препозната као одгледана. Покушај поново.",
  "errors.daily_ad_limit_reached": "Достигао си дневни лимит реклама. Покушај сутра.",

  "share.inviteText": "Играј Мексико са мном!",
  "ad.placeholderText": "Симулирана реклама (самостални веб демо)",
  "rotateOverlay.text": "Окрени телефон да играш Мексико",
  "languageSwitcher.ariaLabel": "Језик",
} satisfies TranslationCatalog;
